import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, Edit, Trash2, Key, RotateCcw, AlertTriangle, Check, X, Eye, EyeOff, Copy, RefreshCw, Wifi, WifiOff, Clock, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function Admin() {
  const [workers, setWorkers] = useState([]);
  const [permissionsCatalog, setPermissionsCatalog] = useState([]);
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [activeTab, setActiveTab] = useState('workers');

  const getHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const fetchAll = useCallback(async () => {
    try {
      const [workersRes, catalogRes, alertsRes] = await Promise.all([
        fetch(`${API}/api/admin/workers`, { headers: getHeaders() }),
        fetch(`${API}/api/admin/permissions-catalog`, { headers: getHeaders() }),
        fetch(`${API}/api/admin/security-alerts`, { headers: getHeaders() }),
      ]);

      if (workersRes.ok) setWorkers(await workersRes.json());
      if (catalogRes.ok) setPermissionsCatalog(await catalogRes.json());
      if (alertsRes.ok) setSecurityAlerts(await alertsRes.json());
    } catch (err) {
      console.error('Error cargando datos admin:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchLogs = async () => {
    try {
      const res = await fetch(`${API}/api/admin/security-logs?limit=200`, { headers: getHeaders() });
      if (res.ok) setSecurityLogs(await res.json());
    } catch (err) {
      console.error('Error cargando logs:', err);
    }
  };

  // === CREATE WORKER ===
  const CreateWorkerModal = () => {
    const [form, setForm] = useState({ username: '', password: '', display_name: '', permissions: [] });
    const [showPass, setShowPass] = useState(false);
    const [saving, setSaving] = useState(false);

    const generatePassword = () => {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
      let pass = '';
      for (let i = 0; i < 12; i++) pass += chars[Math.floor(Math.random() * chars.length)];
      setForm(f => ({ ...f, password: pass }));
    };

    const togglePermission = (key) => {
      setForm(f => ({
        ...f,
        permissions: f.permissions.includes(key)
          ? f.permissions.filter(p => p !== key)
          : [...f.permissions, key]
      }));
    };

    const selectAll = () => setForm(f => ({ ...f, permissions: permissionsCatalog.map(p => p.key) }));
    const clearAll = () => setForm(f => ({ ...f, permissions: [] }));

    const handleCreate = async () => {
      if (!form.username || !form.password || !form.display_name) {
        toast.error('Todos los campos son obligatorios');
        return;
      }
      if (form.permissions.length === 0) {
        toast.error('Debe seleccionar al menos un permiso');
        return;
      }
      setSaving(true);
      try {
        const res = await fetch(`${API}/api/admin/workers`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(form)
        });
        if (res.ok) {
          toast.success(`Trabajador "${form.display_name}" creado exitosamente`);
          setShowCreateModal(false);
          fetchAll();
        } else {
          try {
            const data = await res.json();
            toast.error(data.detail || 'Error al crear trabajador');
          } catch (parseErr) {
            toast.error('Error del servidor: ' + res.status);
          }
        }
      } catch (err) {
        toast.error('Error de conexión: ' + err.message);
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="create-worker-modal">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-500" /> Crear Trabajador
            </h2>
          </div>

          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre completo</label>
                <input data-testid="worker-display-name" type="text" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                  placeholder="Juan Pérez" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Usuario</label>
                <input data-testid="worker-username" type="text" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                  placeholder="juan_poker" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contraseña</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input data-testid="worker-password" type={showPass ? "text" : "password"} value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                    placeholder="Mínimo 8 caracteres" />
                  <button onClick={() => setShowPass(!showPass)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button onClick={generatePassword} className="px-3 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-200 text-sm font-medium whitespace-nowrap">
                  Auto-generar
                </button>
                {form.password && (
                  <button onClick={() => { navigator.clipboard.writeText(form.password); toast.success('Contraseña copiada'); }}
                    className="px-2 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200">
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Permisos de acceso</label>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">Todos</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={clearAll} className="text-xs text-gray-500 hover:underline">Ninguno</button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="permissions-checklist">
                {permissionsCatalog.map(perm => (
                  <label key={perm.key}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      form.permissions.includes(perm.key)
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-500'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}>
                    <input type="checkbox" checked={form.permissions.includes(perm.key)} onChange={() => togglePermission(perm.key)}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{perm.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">{form.permissions.length} de {permissionsCatalog.length} módulos seleccionados</p>
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              Cancelar
            </button>
            <button data-testid="create-worker-submit" onClick={handleCreate} disabled={saving}
              className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-50">
              {saving ? 'Creando...' : 'Crear Trabajador'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // === EDIT WORKER ===
  const EditWorkerModal = () => {
    const [form, setForm] = useState({
      display_name: selectedWorker?.display_name || '',
      permissions: selectedWorker?.permissions || [],
      active: selectedWorker?.active ?? true,
    });
    const [saving, setSaving] = useState(false);
    const [showResetPass, setShowResetPass] = useState(false);
    const [newPassword, setNewPassword] = useState('');

    const togglePermission = (key) => {
      setForm(f => ({
        ...f,
        permissions: f.permissions.includes(key)
          ? f.permissions.filter(p => p !== key)
          : [...f.permissions, key]
      }));
    };

    const handleSave = async () => {
      setSaving(true);
      try {
        const res = await fetch(`${API}/api/admin/workers/${selectedWorker.id}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify(form)
        });
        if (res.ok) {
          toast.success('Trabajador actualizado');
          setShowEditModal(false);
          fetchAll();
        } else {
          const data = await res.json();
          toast.error(data.detail || 'Error al actualizar');
        }
      } catch (err) {
        toast.error('Error de conexión');
      } finally {
        setSaving(false);
      }
    };

    const handleResetPassword = async () => {
      if (!newPassword || newPassword.length < 8) {
        toast.error('La contraseña debe tener al menos 8 caracteres');
        return;
      }
      try {
        const res = await fetch(`${API}/api/admin/workers/${selectedWorker.id}/reset-password`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({ password: newPassword })
        });
        if (res.ok) {
          toast.success('Contraseña reseteada');
          setShowResetPass(false);
          setNewPassword('');
        } else {
          const data = await res.json();
          toast.error(data.detail || 'Error al resetear contraseña');
        }
      } catch (err) {
        toast.error('Error de conexión');
      }
    };

    const handleResetIP = async () => {
      try {
        const res = await fetch(`${API}/api/admin/workers/${selectedWorker.id}/reset-ip`, {
          method: 'POST',
          headers: getHeaders()
        });
        if (res.ok) {
          toast.success('IP reseteada. El próximo login registrará la nueva IP.');
          fetchAll();
        }
      } catch (err) {
        toast.error('Error de conexión');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="edit-worker-modal">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-500" /> Editar: {selectedWorker?.display_name}
            </h2>
          </div>

          <div className="p-6 space-y-5">
            {/* Info del worker */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Usuario:</span>
                <span className="font-mono text-gray-900 dark:text-white">{selectedWorker?.username}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">IP registrada:</span>
                <span className="font-mono text-gray-900 dark:text-white">
                  {selectedWorker?.ip_whitelist?.length > 0 ? selectedWorker.ip_whitelist.join(', ') : 'Sin registrar'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Último acceso:</span>
                <span className="text-gray-900 dark:text-white">
                  {selectedWorker?.last_login ? new Date(selectedWorker.last_login).toLocaleString('es-AR') : 'Nunca'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre</label>
              <input type="text" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </div>

            {/* Estado activo/inactivo */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-600">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Estado de la cuenta</span>
              <button onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  form.active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                {form.active ? 'Activo' : 'Desactivado'}
              </button>
            </div>

            {/* Permisos */}
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">Permisos de acceso</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {permissionsCatalog.map(perm => (
                  <label key={perm.key}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      form.permissions.includes(perm.key)
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-500'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}>
                    <input type="checkbox" checked={form.permissions.includes(perm.key)} onChange={() => togglePermission(perm.key)}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{perm.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Acciones de seguridad */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Acciones de seguridad</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setShowResetPass(!showResetPass)}
                  className="flex items-center gap-2 px-3 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-lg text-sm hover:bg-amber-200">
                  <Key className="w-4 h-4" /> Resetear contraseña
                </button>
                <button onClick={handleResetIP}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm hover:bg-blue-200">
                  <RotateCcw className="w-4 h-4" /> Resetear IP
                </button>
              </div>
              {showResetPass && (
                <div className="flex gap-2 mt-2">
                  <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    placeholder="Nueva contraseña" className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
                  <button onClick={handleResetPassword} className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm">Aplicar</button>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button onClick={() => setShowEditModal(false)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50">
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // === SECURITY LOGS MODAL ===
  const LogsModal = () => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="security-logs-modal">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Registro de Seguridad</h2>
          <button onClick={() => setShowLogsModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-4">
          {securityLogs.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">Sin registros</p>
          ) : (
            <div className="space-y-2">
              {securityLogs.map((log, i) => (
                <div key={i} className={`p-3 rounded-lg border text-sm ${
                  log.blocked
                    ? 'border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800'
                    : 'border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600'
                }`}>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      {log.blocked ? <AlertTriangle className="w-4 h-4 text-red-500" /> : <Check className="w-4 h-4 text-emerald-500" />}
                      <span className="font-medium text-gray-900 dark:text-white">{log.username}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        log.event_type === 'ip_blocked' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        log.event_type === 'first_login' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                      }`}>
                        {log.event_type === 'ip_blocked' ? 'IP BLOQUEADA' :
                         log.event_type === 'first_login' ? 'PRIMER LOGIN' :
                         log.event_type}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(log.timestamp).toLocaleString('es-AR')}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {log.ip}</span>
                    <span>{log.details}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // === MAIN RENDER ===
  const handleDeleteWorker = async (worker) => {
    if (!window.confirm(`¿Desactivar al trabajador "${worker.display_name}"? No podrá acceder al sistema.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/workers/${worker.id}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) {
        toast.success('Trabajador desactivado');
        fetchAll();
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
  };

  const acknowledgeAlerts = async () => {
    try {
      await fetch(`${API}/api/admin/security-alerts/acknowledge`, { method: 'PUT', headers: getHeaders() });
      setSecurityAlerts([]);
      toast.success('Alertas marcadas como leídas');
    } catch (err) {
      toast.error('Error');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Shield className="w-7 h-7 text-indigo-500" /> Panel de Administración
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Gestión de trabajadores, permisos y seguridad</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="view-security-logs-btn" onClick={() => { fetchLogs(); setShowLogsModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm">
            <Clock className="w-4 h-4" /> Registro de seguridad
          </button>
          <button data-testid="create-worker-btn" onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium">
            <Plus className="w-4 h-4" /> Crear Trabajador
          </button>
        </div>
      </div>

      {/* Alertas de seguridad */}
      {securityAlerts.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4" data-testid="security-alerts">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-red-700 dark:text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> {securityAlerts.length} alerta(s) de seguridad
            </h3>
            <button onClick={acknowledgeAlerts} className="text-xs text-red-600 dark:text-red-400 hover:underline">Marcar como leídas</button>
          </div>
          <div className="space-y-2">
            {securityAlerts.slice(0, 5).map((alert, i) => (
              <div key={i} className="flex items-center justify-between text-sm bg-white dark:bg-gray-800 rounded-lg p-3 border border-red-100 dark:border-red-900">
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">{alert.username}</span>
                  <span className="mx-2 text-gray-400">-</span>
                  <span className="text-red-600 dark:text-red-400">IP bloqueada: {alert.ip}</span>
                </div>
                <span className="text-xs text-gray-400">{new Date(alert.timestamp).toLocaleString('es-AR')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Workers Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-testid="workers-table">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">Trabajadores ({workers.length})</h3>
        </div>

        {workers.length === 0 ? (
          <div className="p-12 text-center">
            <Shield className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No hay trabajadores creados</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Crea el primer trabajador con el botón de arriba</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Trabajador</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Permisos</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">IP</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Último acceso</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {workers.map(worker => (
                  <tr key={worker.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{worker.display_name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">@{worker.username}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {worker.permissions?.slice(0, 3).map(p => (
                          <span key={p} className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded text-xs">
                            {permissionsCatalog.find(c => c.key === p)?.label?.split(' ')[0] || p}
                          </span>
                        ))}
                        {worker.permissions?.length > 3 && (
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded text-xs">
                            +{worker.permissions.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {worker.ip_registered ? (
                          <>
                            <Wifi className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="text-xs font-mono text-gray-600 dark:text-gray-400">{worker.ip_whitelist?.[0] || '-'}</span>
                          </>
                        ) : (
                          <>
                            <WifiOff className="w-3.5 h-3.5 text-amber-500" />
                            <span className="text-xs text-amber-600 dark:text-amber-400">Pendiente</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        worker.active
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {worker.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {worker.last_login ? new Date(worker.last_login).toLocaleString('es-AR') : 'Nunca'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button data-testid={`edit-worker-${worker.username}`} onClick={() => { setSelectedWorker(worker); setShowEditModal(true); }}
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg" title="Editar">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button data-testid={`delete-worker-${worker.username}`} onClick={() => handleDeleteWorker(worker)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg" title="Desactivar">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && <CreateWorkerModal />}
      {showEditModal && selectedWorker && <EditWorkerModal />}
      {showLogsModal && <LogsModal />}
    </div>
  );
}
